import { supabase } from './supabase';
import {
  News, Category, WeatherDay, InstagramPost, Quotation,
  PaginatedResponse, LoginRequest, LoginResponse, NewsFormData,
  Project, ProjectFormData, ProjectGallery, ProjectPhoto,
  CotacaoDto, CotacaoComTendenciaDto, CotacaoCreateDto,
} from '../types';

// ── Mappers (snake_case → camelCase) ──────────────────────────────────────────

const mapCategory = (row: any): Category => ({
  id: row.id,
  nome: row.nome,
  descricao: row.descricao,
  ativo: row.ativo,
  dataCriacao: row.data_criacao,
});

const mapNews = (row: any): News => ({
  id: row.id,
  slug: row.slug || String(row.id),
  titulo: row.titulo,
  subtitulo: row.subtitulo,
  texto: row.texto,
  imagemUrl: row.imagem_url,
  videoUrl: row.video_url || undefined,
  dataNoticia: row.data_noticia,
  fonte: row.fonte,
  categoriaId: row.categoria_id,
  categoria: row.categorias ? mapCategory(row.categorias) : undefined,
  idGaleriaFotos: row.galeria_id,
  galeriaFotos: row.galerias
    ? {
        id: row.galerias.id,
        titulo: row.galerias.titulo,
        fotos: ((row.galerias.fotos as any[]) || [])
          .map(mapPhoto)
          .sort((a, b) => a.ordem - b.ordem),
      }
    : undefined,
});

const mapPhoto = (row: any): ProjectPhoto => ({
  id: row.id,
  url: row.url,
  legenda: row.legenda,
  ordem: row.ordem,
  isPrincipal: row.is_principal,
});

const mapProject = (row: any): Project => ({
  id: row.id,
  slug: row.slug || String(row.id),
  titulo: row.titulo,
  descricao: row.descricao,
  dataProjeto: row.data_projeto,
  local: row.local,
  status: row.status,
  dataCriacao: row.data_criacao,
  ativo: row.ativo,
  categoriaLabel: row.categoria_label,
  idGaleriaFotos: row.galeria_id,
  galeriaFotos: row.galerias
    ? {
        id: row.galerias.id,
        titulo: row.galerias.titulo,
        fotos: ((row.galerias.fotos as any[]) || [])
          .map(mapPhoto)
          .sort((a, b) => a.ordem - b.ordem),
      }
    : undefined,
});

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.login,
      password: credentials.senha,
    });

    if (error || !data.session) {
      throw new Error(error?.message || 'Credenciais inválidas');
    }

    localStorage.setItem('admin_token', data.session.access_token);
    const role = data.user.email === 'agricampanha@gmail.com' ? 'viewer' : 'admin';
    localStorage.setItem('admin_role', role);
    return { success: true, token: data.session.access_token };
  },

  logout: async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
  },
};

// ── Categories ────────────────────────────────────────────────────────────────

export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    const { data, error } = await supabase
      .from('categorias')
      .select('*')
      .order('nome');
    if (error) throw new Error(error.message);
    return (data || []).map(mapCategory);
  },

  getActive: async (): Promise<Category[]> => {
    const { data, error } = await supabase
      .from('categorias')
      .select('*')
      .eq('ativo', true)
      .order('nome');
    if (error) return [];
    return (data || []).map(mapCategory);
  },

  getById: async (id: number): Promise<Category> => {
    const { data, error } = await supabase
      .from('categorias')
      .select('*')
      .eq('id', id)
      .single();
    if (error || !data) throw new Error('Category not found');
    return mapCategory(data);
  },

  create: async (category: Omit<Category, 'id' | 'dataCriacao'>): Promise<Category> => {
    const { data, error } = await supabase
      .from('categorias')
      .insert({ nome: category.nome, descricao: category.descricao, ativo: category.ativo })
      .select()
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to create category');
    return mapCategory(data);
  },

  update: async (id: number, category: Partial<Category>): Promise<Category> => {
    const { data, error } = await supabase
      .from('categorias')
      .update({ nome: category.nome, descricao: category.descricao, ativo: category.ativo })
      .eq('id', id)
      .select()
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to update category');
    return mapCategory(data);
  },

  delete: async (id: number): Promise<void> => {
    const { error } = await supabase.from('categorias').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },
};

// ── News ──────────────────────────────────────────────────────────────────────

export const newsApi = {
  getAll: async (page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<News>> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('noticias')
      .select('*, categorias(*)', { count: 'exact' })
      .eq('ativo', true)
      .order('data_noticia', { ascending: false })
      .range(from, to);

    if (error) throw new Error(error.message);
    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      items: (data || []).map(mapNews),
      currentPage: page,
      pageSize,
      totalCount,
      totalPages,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
    };
  },

  getLatest: async (limit: number = 4): Promise<News[]> => {
    const { data, error } = await supabase
      .from('noticias')
      .select('*, categorias(*)')
      .eq('ativo', true)
      .order('data_noticia', { ascending: false })
      .limit(limit);
    if (error) return [];
    return (data || []).map(mapNews);
  },

  getById: async (id: number): Promise<News> => {
    const { data, error } = await supabase
      .from('noticias')
      .select('*, categorias(*), galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .eq('id', id)
      .single();
    if (error || !data) throw new Error('News not found');
    return mapNews(data);
  },

  getBySlug: async (slug: string): Promise<News> => {
    const { data, error } = await supabase
      .from('noticias')
      .select('*, categorias(*), galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .eq('slug', slug)
      .eq('ativo', true)
      .single();
    if (error || !data) throw new Error('News not found');
    return mapNews(data);
  },

  slugExists: async (slug: string): Promise<boolean> => {
    const { data } = await supabase
      .from('noticias')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    return !!data;
  },

  getByCategory: async (
    categoryId: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<News>> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('noticias')
      .select('*, categorias(*)', { count: 'exact' })
      .eq('ativo', true)
      .eq('categoria_id', categoryId)
      .order('data_noticia', { ascending: false })
      .range(from, to);

    if (error) throw new Error(error.message);
    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      items: (data || []).map(mapNews),
      currentPage: page,
      pageSize,
      totalCount,
      totalPages,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
    };
  },

  search: async (
    query: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedResponse<News>> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('noticias')
      .select('*, categorias(*)', { count: 'exact' })
      .eq('ativo', true)
      .or(`titulo.ilike.%${query}%,texto.ilike.%${query}%`)
      .order('data_noticia', { ascending: false })
      .range(from, to);

    if (error) {
      return {
        items: [], currentPage: page, pageSize,
        totalCount: 0, totalPages: 0, hasPreviousPage: false, hasNextPage: false,
      };
    }

    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / pageSize);
    return {
      items: (data || []).map(mapNews),
      currentPage: page, pageSize, totalCount, totalPages,
      hasPreviousPage: page > 1, hasNextPage: page < totalPages,
    };
  },

  create: async (news: NewsFormData): Promise<News> => {
    const { data: galeria } = await supabase
      .from('galerias')
      .insert({ titulo: news.titulo })
      .select()
      .single();

    const { data, error } = await supabase
      .from('noticias')
      .insert({
        titulo: news.titulo,
        slug: news.slug,
        subtitulo: news.subtitulo,
        texto: news.texto,
        imagem_url: news.imagemUrl,
        video_url: news.videoUrl || null,
        data_noticia: news.dataNoticia,
        fonte: news.fonte,
        categoria_id: news.categoriaId,
        galeria_id: galeria?.id ?? null,
        ativo: true,
      })
      .select('*, categorias(*), galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .single();

    if (error || !data) throw new Error(error?.message || 'Failed to create news');
    return mapNews(data);
  },

  update: async (id: number, news: Partial<NewsFormData & { idGaleriaFotos?: number }>): Promise<News> => {
    const updateData: Record<string, any> = {};
    if (news.titulo !== undefined) updateData.titulo = news.titulo;
    if (news.slug !== undefined) updateData.slug = news.slug;
    if (news.subtitulo !== undefined) updateData.subtitulo = news.subtitulo;
    if (news.texto !== undefined) updateData.texto = news.texto;
    if (news.imagemUrl !== undefined) updateData.imagem_url = news.imagemUrl;
    if (news.videoUrl !== undefined) updateData.video_url = news.videoUrl || null;
    if (news.dataNoticia !== undefined) updateData.data_noticia = news.dataNoticia;
    if (news.fonte !== undefined) updateData.fonte = news.fonte;
    if (news.categoriaId !== undefined) updateData.categoria_id = news.categoriaId;
    if (news.idGaleriaFotos !== undefined) updateData.galeria_id = news.idGaleriaFotos;

    const { data, error } = await supabase
      .from('noticias')
      .update(updateData)
      .eq('id', id)
      .select('*, categorias(*), galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to update news');
    return mapNews(data);
  },

  delete: async (id: number): Promise<void> => {
    const { error } = await supabase.from('noticias').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  uploadImage: async (file: File): Promise<{ url: string }> => {
    const ext = file.name.split('.').pop();
    const path = `${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from('news').upload(path, file);
    if (error || !data) throw new Error(error?.message || 'Erro ao fazer upload');
    const { data: { publicUrl } } = supabase.storage.from('news').getPublicUrl(data.path);
    return { url: publicUrl };
  },
};

// ── Projects ──────────────────────────────────────────────────────────────────

export const projectsApi = {
  getAll: async (): Promise<Project[]> => {
    const { data, error } = await supabase
      .from('projetos')
      .select('*, galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .eq('ativo', true)
      .order('data_projeto', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(mapProject);
  },

  getById: async (id: number): Promise<Project> => {
    const { data, error } = await supabase
      .from('projetos')
      .select('*, galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .eq('id', id)
      .single();
    if (error || !data) throw new Error('Project not found');
    return mapProject(data);
  },

  getBySlug: async (slug: string): Promise<Project> => {
    const { data, error } = await supabase
      .from('projetos')
      .select('*, galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .eq('slug', slug)
      .eq('ativo', true)
      .single();
    if (error || !data) throw new Error('Project not found');
    return mapProject(data);
  },

  slugExists: async (slug: string): Promise<boolean> => {
    const { data } = await supabase
      .from('projetos')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    return !!data;
  },

  create: async (project: ProjectFormData): Promise<Project> => {
    // Create a gallery for the new project
    const { data: galeria } = await supabase
      .from('galerias')
      .insert({ titulo: project.titulo })
      .select()
      .single();

    const { data, error } = await supabase
      .from('projetos')
      .insert({
        titulo: project.titulo,
        slug: project.slug,
        descricao: project.descricao,
        data_projeto: project.dataProjeto,
        local: project.local,
        status: project.status,
        ativo: project.ativo ?? true,
        categoria_label: project.categoriaLabel ?? 'Projeto',
        galeria_id: galeria?.id ?? null,
      })
      .select('*, galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .single();

    if (error || !data) throw new Error(error?.message || 'Failed to create project');
    return mapProject(data);
  },

  update: async (id: number, project: Partial<ProjectFormData>): Promise<Project> => {
    const updateData: Record<string, any> = {};
    if (project.titulo !== undefined) updateData.titulo = project.titulo;
    if (project.slug !== undefined) updateData.slug = project.slug;
    if (project.descricao !== undefined) updateData.descricao = project.descricao;
    if (project.dataProjeto !== undefined) updateData.data_projeto = project.dataProjeto;
    if (project.local !== undefined) updateData.local = project.local;
    if (project.status !== undefined) updateData.status = project.status;
    if (project.ativo !== undefined) updateData.ativo = project.ativo;
    if (project.categoriaLabel !== undefined) updateData.categoria_label = project.categoriaLabel;

    const { data, error } = await supabase
      .from('projetos')
      .update(updateData)
      .eq('id', id)
      .select('*, galerias(id, titulo, fotos(id, url, legenda, ordem, is_principal))')
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to update project');
    return mapProject(data);
  },

  delete: async (id: number): Promise<void> => {
    const { error } = await supabase.from('projetos').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },
};

// ── Gallery ───────────────────────────────────────────────────────────────────

export const galleryApi = {
  getAll: async (page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<any>> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('galerias')
      .select('*, fotos(*)', { count: 'exact' })
      .range(from, to);

    if (error) {
      return {
        items: [], currentPage: page, pageSize,
        totalCount: 0, totalPages: 0, hasPreviousPage: false, hasNextPage: false,
      };
    }
    const totalCount = count || 0;
    const totalPages = Math.ceil(totalCount / pageSize);
    return {
      items: data || [], currentPage: page, pageSize, totalCount, totalPages,
      hasPreviousPage: page > 1, hasNextPage: page < totalPages,
    };
  },

  getById: async (id: number): Promise<ProjectGallery> => {
    const { data, error } = await supabase
      .from('galerias')
      .select('*, fotos(*)')
      .eq('id', id)
      .single();
    if (error || !data) throw new Error('Gallery not found');
    return { id: data.id, titulo: data.titulo, fotos: (data.fotos || []).map(mapPhoto) };
  },

  getByReference: async (_idReferencia: number, _tipo: string = 'projeto'): Promise<any[]> => {
    return [];
  },

  create: async (gallery: Omit<ProjectGallery, 'id' | 'fotos'>): Promise<ProjectGallery> => {
    const { data, error } = await supabase
      .from('galerias')
      .insert({ titulo: gallery.titulo })
      .select()
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to create gallery');
    return { id: data.id, titulo: data.titulo, fotos: [] };
  },

  update: async (id: number, gallery: Partial<ProjectGallery>): Promise<ProjectGallery> => {
    const { data, error } = await supabase
      .from('galerias')
      .update({ titulo: gallery.titulo })
      .eq('id', id)
      .select('*, fotos(*)')
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to update gallery');
    return { id: data.id, titulo: data.titulo, fotos: (data.fotos || []).map(mapPhoto) };
  },

  delete: async (id: number): Promise<void> => {
    const { error } = await supabase.from('galerias').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  updatePhoto: async (id: number, data: { ordem?: number; isPrincipal?: boolean; legenda?: string }): Promise<void> => {
    const updateData: Record<string, any> = {};
    if (data.ordem !== undefined) updateData.ordem = data.ordem;
    if (data.isPrincipal !== undefined) updateData.is_principal = data.isPrincipal;
    if (data.legenda !== undefined) updateData.legenda = data.legenda;
    const { error } = await supabase.from('fotos').update(updateData).eq('id', id);
    if (error) throw new Error(error.message);
  },

  addPhoto: async (
    galleryId: number,
    photoData: { url: string; ordem: number; isPrincipal: boolean; legenda: string }
  ): Promise<any> => {
    const { data, error } = await supabase
      .from('fotos')
      .insert({
        galeria_id: galleryId,
        url: photoData.url,
        legenda: photoData.legenda,
        ordem: photoData.ordem,
        is_principal: photoData.isPrincipal,
      })
      .select()
      .single();
    if (error || !data) throw new Error(error?.message || 'Failed to add photo');
    return mapPhoto(data);
  },

  deletePhoto: async (imageUrl: string): Promise<void> => {
    const { error } = await supabase.from('fotos').delete().eq('url', imageUrl);
    if (error) throw new Error(error.message);
  },

  uploadPhoto: async (file: File): Promise<{ url: string }> => {
    const ext = file.name.split('.').pop();
    const path = `${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage.from('projects').upload(path, file);
    if (error || !data) throw new Error(error?.message || 'Erro ao fazer upload');
    const { data: { publicUrl } } = supabase.storage.from('projects').getPublicUrl(data.path);
    return { url: publicUrl };
  },

  uploadMultiplePhotos: async (files: File[]): Promise<{ url: string }[]> => {
    return Promise.all(files.map(f => galleryApi.uploadPhoto(f)));
  },
};

// ── Quotations ────────────────────────────────────────────────────────────────

const computeTendency = (
  current: number | null,
  previous: number | null
): 'up' | 'down' | 'stable' => {
  if (current == null || previous == null) return 'stable';
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'stable';
};

export const quotationsApi = {
  getRecent: async (): Promise<CotacaoComTendenciaDto | null> => {
    const { data, error } = await supabase
      .from('cotacoes')
      .select('*')
      .order('data_cadastro', { ascending: false })
      .limit(2);

    if (error || !data || data.length === 0) return null;

    const current = data[0];
    const prev = data[1] ?? null;

    return {
      idCotacao: current.id,
      soja: current.soja,
      arroz: current.arroz,
      milho: current.milho,
      dataCadastro: current.data_cadastro,
      tendenciaSoja: computeTendency(current.soja, prev?.soja ?? null),
      tendenciaArroz: computeTendency(current.arroz, prev?.arroz ?? null),
      tendenciaMilho: computeTendency(current.milho, prev?.milho ?? null),
    };
  },

  getAll: async (): Promise<CotacaoComTendenciaDto[]> => {
    const { data, error } = await supabase
      .from('cotacoes')
      .select('*')
      .order('data_cadastro', { ascending: false })
      .limit(30);

    if (error || !data) return [];

    return data.map((row, i) => ({
      idCotacao: row.id,
      soja: row.soja,
      arroz: row.arroz,
      milho: row.milho,
      dataCadastro: row.data_cadastro,
      tendenciaSoja: computeTendency(row.soja, data[i + 1]?.soja ?? null),
      tendenciaArroz: computeTendency(row.arroz, data[i + 1]?.arroz ?? null),
      tendenciaMilho: computeTendency(row.milho, data[i + 1]?.milho ?? null),
    }));
  },

  create: async (cotacao: CotacaoCreateDto): Promise<CotacaoDto | null> => {
    const { data, error } = await supabase
      .from('cotacoes')
      .insert({ soja: cotacao.soja, arroz: cotacao.arroz, milho: cotacao.milho })
      .select()
      .single();

    if (error || !data) {
      console.error('Error creating quotation:', error);
      return null;
    }

    return {
      idCotacao: data.id,
      soja: data.soja,
      arroz: data.arroz,
      milho: data.milho,
      dataCadastro: data.data_cadastro,
    };
  },

  convertToQuotations: (cotacao: CotacaoComTendenciaDto): Quotation[] => {
    const getTrendChange = (trend: 'up' | 'down' | 'stable' | null): number => {
      if (trend === 'up') return 1;
      if (trend === 'down') return -1;
      return 0;
    };

    return [
      {
        name: 'Soja',
        value: cotacao.soja,
        unit: 'R$/sc 60kg',
        icon: '🌱',
        image: '/img/soja.jpg',
        change: getTrendChange(cotacao.tendenciaSoja),
      },
      {
        name: 'Arroz',
        value: cotacao.arroz,
        unit: 'R$/sc 50kg',
        icon: '🌾',
        image: '/img/arroz.jpg',
        change: getTrendChange(cotacao.tendenciaArroz),
      },
      {
        name: 'Milho',
        value: cotacao.milho,
        unit: 'R$/sc 60kg',
        icon: '🌽',
        image: '/img/milho.jpg',
        change: getTrendChange(cotacao.tendenciaMilho),
      },
    ];
  },

  delete: async (id: number): Promise<void> => {
    const { error } = await supabase.from('cotacoes').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  getCurrentQuotations: async (): Promise<Quotation[]> => {
    try {
      const cotacao = await quotationsApi.getRecent();
      if (!cotacao) return [];
      return quotationsApi.convertToQuotations(cotacao);
    } catch (error) {
      console.error('Error getting current quotations:', error);
      return [];
    }
  },
};

// ── Weather (external Open Meteo API) ─────────────────────────────────────────

export const weatherApi = {
  getCurrentWeather: async (): Promise<WeatherDay[]> => {
    try {
      const latitude = -31.3314;
      const longitude = -54.1069;
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max&timezone=America/Sao_Paulo&forecast_days=7`;

      const response = await fetch(url);
      if (!response.ok) return getMockWeather();

      const data = await response.json();
      const weatherDays: WeatherDay[] = [];

      for (let i = 0; i < data.daily.time.length; i++) {
        weatherDays.push({
          date: data.daily.time[i],
          tempMax: data.daily.temperature_2m_max[i] || 0,
          tempMin: data.daily.temperature_2m_min[i] || 0,
          precipitation: data.daily.precipitation_sum[i] || 0,
          precipProbability: data.daily.precipitation_probability_max[i] || 0,
          windSpeed: data.daily.wind_speed_10m_max[i] || 0,
          humidity: 70,
        });
      }

      return weatherDays;
    } catch (error) {
      console.error('Error fetching weather:', error);
      return getMockWeather();
    }
  },
};

function getMockWeather(): WeatherDay[] {
  const today = new Date();
  const days: WeatherDay[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    days.push({
      date: date.toISOString().split('T')[0],
      tempMin: 15 + Math.floor(Math.random() * 5),
      tempMax: 25 + Math.floor(Math.random() * 10),
      precipitation: Math.floor(Math.random() * 100),
      windSpeed: 10 + Math.floor(Math.random() * 15),
      humidity: 60 + Math.floor(Math.random() * 20),
      precipProbability: Math.floor(Math.random() * 100),
    });
  }

  return days;
}

// ── Instagram (external API) ──────────────────────────────────────────────────

export const instagramApi = {
  getLatestPosts: async (limit: number = 6): Promise<InstagramPost[]> => {
    try {
      const instagramApiUrl = process.env.REACT_APP_INSTAGRAM_API_URL;

      if (instagramApiUrl) {
        const response = await fetch(`${instagramApiUrl}?limit=${limit}`);
        if (response.ok) {
          const data = await response.json();
          return data
            .sort((a: InstagramPost, b: InstagramPost) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            )
            .slice(0, limit);
        }
      }

      return getMockInstagramPosts(limit);
    } catch (error) {
      console.error('Error fetching Instagram posts:', error);
      return getMockInstagramPosts(limit);
    }
  },
};

function getMockInstagramPosts(limit: number): InstagramPost[] {
  return Array.from({ length: limit }, (_, i) => ({
    id: String(i + 1),
    caption: `Post ${i + 1}`,
    mediaUrl: `https://via.placeholder.com/400x400?text=Post+${i + 1}`,
    permalink: 'https://instagram.com',
    timestamp: new Date(Date.now() - i * 86400000).toISOString(),
  }));
}
